import { useEffect, useRef } from "react";
import {
  Button,
  Cluster,
  CopyButton,
  Field,
  Icon,
  IconButton,
  Input,
  Panel,
  Scrim,
  Subtitle,
  Thinking,
  Title,
} from "@fwf/fwfa-design-system/react";
import type { AuthChallenge } from "@/types";

interface Props {
  challenge: AuthChallenge;
  onShowTerminal: () => void;
  onDismiss: () => void;
}

export function AuthDialog({ challenge, onShowTerminal, onDismiss }: Props) {
  const hasBrowserUrl = Boolean(challenge.url?.trim());
  const dialogRef = useRef<HTMLDivElement>(null);

  // Escape closes, matching what DS Modal does for a dialog. Modal itself is not
  // usable here: it portals into document.body, which would tear both this dialog
  // and TerminalAuthCard out of the contained stage frames on /design-system and
  // paint them over the whole page. Composing Scrim + Panel keeps the overlay where
  // its render site puts it. Backdrop click is deliberately NOT a dismiss — losing
  // a one-time code to a stray click mid-sign-in is worse than an extra keystroke.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      onDismiss();
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [onDismiss]);

  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  return (
    <Scrim>
      {/* Dialog semantics sit on a wrapper rather than on Panel itself: the DS
          wrappers are typed ComponentPropsWithoutRef, so Panel cannot take the ref
          this needs to move focus, even though React 19 would pass it through. */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="auth-dialog-title"
        tabIndex={-1}
        // min-w-0 clears the grid item's automatic minimum size. Scrim centres its
        // child in a grid, so without this the dialog is floored at its min-content
        // width and a single nowrap control can push it wider than a phone viewport.
        className="w-full min-w-0 max-w-md"
      >
        <Panel className="animate-scale-in flex flex-col gap-4 bg-surface-opaque shadow-float">
          <div className="flex items-start justify-between gap-3">
            {/* flex-nowrap so a title that wraps on a phone stays beside the glyph
                instead of dropping to its own line under it. */}
            <Cluster className="flex-nowrap items-start gap-2">
              <Icon aria-hidden="true">
                <svg
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z"
                  />
                </svg>
              </Icon>
              <Title
                as="h2"
                id="auth-dialog-title"
                className="fwfa-title--component"
              >
                Authentication required
              </Title>
            </Cluster>
            <IconButton label="Dismiss" compact onClick={onDismiss}>
              <Icon aria-hidden="true">
                <svg
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18 18 6M6 6l12 12"
                  />
                </svg>
              </Icon>
            </IconButton>
          </div>

          <Subtitle className="fwfa-subtitle--sm">
            {challenge.agentName} needs you to sign in.
          </Subtitle>

          {challenge.code && (
            <div className="flex flex-col gap-2">
              <Field label="Your one-time code">
                <Input
                  readOnly
                  value={challenge.code}
                  onFocus={(event) => event.currentTarget.select()}
                  className="text-center font-mono font-semibold tracking-widest"
                />
              </Field>
              <CopyButton
                text={challenge.code}
                variant={hasBrowserUrl ? "secondary" : "primary"}
                className="w-full"
              >
                Copy code
              </CopyButton>
            </div>
          )}

          {hasBrowserUrl && (
            <Button
              variant="primary"
              className="w-full"
              onClick={() => window.freshvibe.openExternal(challenge.url)}
              leftIcon={
                <Icon aria-hidden="true">
                  <svg
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
                    />
                  </svg>
                </Icon>
              }
            >
              Open browser to sign in
            </Button>
          )}

          <Cluster className="gap-2 text-xs text-ink-muted">
            <Thinking label="Waiting for you to finish signing in" />
            <span aria-hidden="true">Waiting for you to complete sign-in…</span>
          </Cluster>

          {/* The prompt is prose and the action is a control: DS buttons are
              white-space: nowrap, so keeping the whole sentence in the button floored
              the dialog at 405px and overflowed a 390px viewport. */}
          <Cluster className="gap-2 border-t border-line pt-4 text-xs text-ink-muted">
            <span>Having trouble?</span>
            <Button variant="ghost" compact onClick={onShowTerminal}>
              Show terminal
            </Button>
          </Cluster>
        </Panel>
      </div>
    </Scrim>
  );
}
